import { formatDistanceToNow, differenceInHours } from "date-fns";
import { es } from "date-fns/locale";
import { Post } from "./types";
import { Sparkles } from "lucide-react";

interface PostCardProps {
  post: Post;
  onClick: (post: Post) => void;
  index?: number;
}

export const PostCard = ({ post, onClick, index = 0 }: PostCardProps) => {
  const formatDate = (date: string) => {
    return formatDistanceToNow(new Date(date), { 
      addSuffix: true,
      locale: es 
    });
  };

  const isNew = differenceInHours(new Date(), new Date(post.created_at)) < 24;

  return (
    <article
      onClick={() => onClick(post)}
      className="relative overflow-hidden rounded-2xl transition-all duration-300 animate-slide-up opacity-0 group h-[180px] sm:h-[250px] cursor-pointer hover:scale-[1.02] hover:shadow-xl hover:shadow-primary/10"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {/* Background image with enhanced overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-500 group-hover:scale-110"
        style={{ 
          backgroundImage: post.image_url 
            ? `url(${post.image_url})` 
            : 'linear-gradient(135deg, hsl(var(--primary) / 0.2), hsl(var(--accent) / 0.1))'
        }}
      />
      
      {/* Multi-layer gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      {/* New badge */}
      {isNew && (
        <div className="absolute top-3 left-3 z-10">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary text-primary-foreground animate-pulse-glow">
            <Sparkles className="h-3 w-3" />
            Nuevo
          </span>
        </div>
      )}
      
      {/* Content */}
      <div className="relative h-full p-4 flex flex-col justify-end">
        <div className="flex items-baseline gap-2 mb-2 flex-wrap">
          <h3 className="font-heading text-base sm:text-lg text-white font-semibold line-clamp-1 group-hover:text-primary-foreground transition-colors">
            {post.title}
          </h3>
          <span className="text-xs text-white/60 whitespace-nowrap">
            · {formatDate(post.created_at)}
          </span>
        </div>
        <p className="text-sm sm:text-base text-white/80 line-clamp-2 group-hover:text-white/90 transition-colors">
          {post.content}
        </p>
        
        {/* Read more indicator */}
        <div className="mt-3 flex items-center gap-1 text-xs text-primary-foreground/80 opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-2 group-hover:translate-y-0 duration-300">
          <span>Leer más</span>
          <span className="text-lg">→</span>
        </div>
      </div>
    </article>
  );
};
